import { Controller } from "@hotwired/stimulus"

// Cableado wallet A: Card Payment Brick (cardPayment, PCI). El cardFormData va a
// process_payment (payment.create con three_d_secure_mode: 'optional'). SIEMPRE
// redirige a la página de resultado:
//   • approved → flujo propio (página de créditos)
//   • pending / rejected / 3DS challenge → el Status Screen Brick los muestra allá
//     (vista nativa de MP; el creq/external_resource_url del 3DS viaja en la URL)
//   • sin payment_id (error de sistema, ej. 401) → status=error (NO es un rechazo)
// Container/IDs propios para no chocar con el cardPaymentBrick_container de subscription.
export default class extends Controller {
  static values = {
    publicKey: String,
    amount: Number,
    email: String,
    processUrl: String,
    resultUrl: String,
    packKey: String,
    storeSlug: String
  }

  async connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    if (typeof window.MercadoPago === "undefined") {
      console.error("[credit-pack-brick] SDK de MercadoPago no cargado")
      return
    }
    if (!this.publicKeyValue || !this.element.id) {
      console.error("[credit-pack-brick] Falta public key o id del container")
      return
    }

    this.brickController = null
    this.unmount = this.unmount.bind(this)
    this.element.innerHTML = "" // limpia residuo del cache de Turbo

    try {
      const mp = new window.MercadoPago(this.publicKeyValue, { locale: "es-CL" })
      this.createPromise = this.renderBrick(mp.bricks())
      await this.createPromise
    } catch (error) {
      console.error("[credit-pack-brick] Falló la inicialización:", error)
    }

    document.addEventListener("turbo:before-visit", this.unmount)
    window.addEventListener("beforeunload", this.unmount)
  }

  async disconnect() {
    if (this.createPromise) {
      try { await this.createPromise } catch (_) {}
      this.createPromise = null
    }
    this.unmount()
    document.removeEventListener("turbo:before-visit", this.unmount)
    window.removeEventListener("beforeunload", this.unmount)
  }

  async renderBrick(bricksBuilder) {
    const settings = {
      initialization: {
        amount: this.amountValue,
        payer: { email: this.emailValue }
      },
      customization: {
        visual: { style: { theme: "bootstrap" } },
        paymentMethods: { maxInstallments: 1 }
      },
      callbacks: {
        onReady: () => {},
        onSubmit: (cardFormData) => this.onSubmit(cardFormData),
        onError: (error) => console.error("[credit-pack-brick] Error en Brick:", error)
      }
    }

    this.brickController = await bricksBuilder.create("cardPayment", this.element.id, settings)
  }

  onSubmit(cardFormData) {
    return new Promise((resolve, reject) => {
      if (!cardFormData?.token) {
        reject("No se generó el token de tarjeta")
        return
      }

      fetch(this.processUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content")
        },
        body: JSON.stringify(cardFormData)
      })
        .then((response) => response.json())
        .then((result) => {
          const id = result.payment_id

          if (result.status === "approved") {
            window.location.href = this.buildResultUrl("approved", id)
          } else if (id) {
            // pending / rejected / 3DS → el Status Screen Brick muestra el estado real.
            let url = this.buildResultUrl(result.status, id)
            if (result.three_ds_info) {
              url += `&external_resource_url=${encodeURIComponent(result.three_ds_info.external_resource_url)}` +
                     `&creq=${encodeURIComponent(result.three_ds_info.creq)}`
            }
            window.location.href = url
          } else {
            // Error de sistema (sin payment_id, ej. 401): NO es un rechazo de tarjeta.
            window.location.href = this.buildResultUrl("error", null)
          }

          resolve(result)
        })
        .catch((error) => {
          console.error("[credit-pack-brick] Error procesando el pago:", error)
          reject(error)
        })
    })
  }

  // URL de result con pack/store, para que la página de resultado pueda ofrecer
  // "Reintentar el pago" volviendo al form del mismo pack (caso rejected).
  buildResultUrl(status, paymentId) {
    let url = `${this.resultUrlValue}?status=${status}`
    if (paymentId) url += `&payment_id=${paymentId}`
    if (this.packKeyValue) url += `&pack_key=${encodeURIComponent(this.packKeyValue)}`
    if (this.storeSlugValue) url += `&store=${encodeURIComponent(this.storeSlugValue)}`
    return url
  }

  unmount() {
    if (this.brickController) {
      try { this.brickController.unmount() } catch (_) {}
      this.brickController = null
    }
    if (this.element) this.element.innerHTML = ""
  }
}
